
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DataConnection, Job, Pageable } from '@nimmsta/ng-commons-lib/models';
import { NimmstaDataSource } from '@nimmsta/ng-commons-lib/data-table';
import { safeEvalWithParams } from '@nimmsta/ng-commons-lib/utils/eval';
import { SFSOptions } from '@nimmsta/ng-commons-lib/data-access';
import { DataConnectionService } from './dataConnection.service';
import { JobService } from './job.service';

export class ListEndpointDataSource extends NimmstaDataSource<Job> {
  public dataConnection?: DataConnection;
  private dataConnectionService: DataConnectionService;

  constructor(jobService: JobService, dataConnectionService: DataConnectionService) {
    super(jobService);
    this.dataConnectionService = dataConnectionService;
    this.loadingSubject.next(false);
  }

  override loadData(options: Partial<SFSOptions> = {}): Observable<Pageable<Job>> {
    if (options.removeDataOnLoad) {
      this.dataSubject.next([]);
    }
    if (this.dataConnection?.dataConnectionHandler === 'CSVDataConnection') {
      const csvFilters = [
        ...(options.filter ?? []),
        'status:OPEN',
        `dataConnection.id:${this.dataConnection.id}`,
      ];
      return super.loadData({
        ...options,
        filter: [...new Set(csvFilters)],
      });
    }
    const jobListEndpoint = this.dataConnection?.endpoints.find(
      (endpoint) => endpoint.identifier === this.dataConnection?.configuration.jobListEndpoint,
    );
    if (jobListEndpoint) {
      const query: Record<string, string> = {
        pageNumber: `${options.pageNumber ?? 0}`,
        pageSize: `${options.pageSize ?? 20}`,
        search: `${options.search ?? ''}`,
      };
      options.resultFilters?.forEach((value, index) => {
        query[`resultFilters${index}`] = value;
      });
      options.filter?.forEach((value, index) => {
        query[`filters${index}`] = value;
      });
      const request = {
        body: jobListEndpoint.body,
        query,
        header: {},
      };
      this.loadingSubject.next(true);
      // Chain preprocess, executeEndpoint, and postprocess and return the result
      return safeEvalWithParams(jobListEndpoint.preprocess, { request }).pipe(
        switchMap((newRequest) => {
          const encodedRequest = this.encodeRequestQueryValues(newRequest);
          return this.dataConnectionService.executeEndpoint(
            this.dataConnection!.identifier,
            jobListEndpoint!.identifier,
            encodedRequest,
          ).pipe(
          switchMap(
            (data) => safeEvalWithParams(jobListEndpoint.postprocess, { response: JSON.parse(data) }).pipe(map((array) => {
              const totalPages = Math.ceil(array.length / options.pageSize!);
              const pageData = {
                content: array.slice(options.pageNumber! * options.pageSize!, (options.pageNumber! + 1) * options.pageSize!),
                totalElements: array.length,
                totalPages,
                first: options.pageNumber === 0,
                last: options.pageNumber === totalPages - 1,
                size: array.length,
                number: options.pageNumber,
              } as Pageable<Job>;
              const processedData = pageData;
              this.data = processedData;
              this.count = processedData.totalElements;
              this.dataSubject.next(processedData.content);
              this.loadingSubject.next(false);
              return processedData;
            })),
          ),
          );
        }),
      );
    } else {
      return of({} as Pageable<Job>);
    }
  }

  reset(): void {
    this.data = undefined;
    this.dataSubject.next([]);
    this.count = 0;
  }

  private encodeRequestQueryValues(request: unknown): unknown {
    if (!request || typeof request !== 'object') {
      return request;
    }

    const requestRecord = request as Record<string, unknown>;
    const query = requestRecord['query'];
    if (!query || typeof query !== 'object' || Array.isArray(query)) {
      return request;
    }

    const encodedQuery = Object.fromEntries(
      Object.entries(query as Record<string, unknown>).map(([key, value]) => [
        key,
        encodeURIComponent(String(value ?? '')),
      ]),
    );

    return {
      ...requestRecord,
      query: encodedQuery,
    };
  }
}
